<template>
  <div class="tw-mb-9">
    <span>
      {{ t("apply-branding") }}
      <a
        :href="EXTERNAL_DOCS_LINKS.MSP_BRANDING_LEARN_MORE"
        target="_blank"
        class="tw-inline-block tw-underline"
      >
        <span class="tw-flex tw-gap-1 tw-items-center">
          {{ t("learn-more") }}
        </span>
      </a>
    </span>
  </div>
  <section class="tw-mb-4">
    <div class="tw-flex tw-flex-col tw-gap-2">
      <branding-tabs v-model="activeTab" />

      <template v-if="activeTab === 'branding-tab-company-name'">
        <branding-company-name-index @has-changed="handleHasChanged" />
      </template>

      <template v-if="activeTab === 'branding-tab-company-logo'">
        <branding-company-logo-index @has-changed="handleHasChanged" />
      </template>
    </div>
  </section>
  <t-delete-confirm
    :visible="isUnsavedChangesModalVisible"
    @on-close="handleStayWithChanges"
    :data-test-id="'msp-branding-leave-without-applying-notice-modal'"
  >
    <template #title>
      <cl-heading size="h4">{{
        $t("leave-without-applying-brand")
      }}</cl-heading>
    </template>

    <template #body>
      <cl-p>{{ $t("leave-branding-prompt") }}</cl-p>
    </template>

    <template #decline>
      <cl-button
        variant="danger-outline"
        @click="handleLeaveWithoutApplyingChanges"
      >
        <span>{{ t("leave") }}</span>
      </cl-button>
    </template>

    <template #confirm>
      <cl-button variant="secondary" @click="handleStayWithChanges">
        <span>{{ t("stay") }}</span>
      </cl-button>
    </template>
  </t-delete-confirm>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useAuthenticationStore } from "@/store/authentication.store";
import { useRouter, useRoute, onBeforeRouteLeave } from "vue-router";
import BrandingTabs, {
  BrandingTabType,
} from "@/modules/Settings/views/Msp/Branding/branding-tabs/branding-tabs.index.vue";
import BrandingCompanyLogoIndex from "@/modules/Settings/views/Msp/Branding/branding-company-logo/branding-company-logo.index.vue";
import BrandingCompanyNameIndex from "./branding-company-name/branding-company-name.index.vue";
import TDeleteConfirm from "@/modals/TDeleteConfirm.vue";
import { EXTERNAL_DOCS_LINKS } from "@/constants/externalDocsLinks.ts";
import { useBrandingStore } from "@/store/branding.store";
import { usePageMetaStore } from "@/store/pageMeta.store";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const authStore = useAuthenticationStore();
const pageMetaStore = usePageMetaStore();
const brandingStore = useBrandingStore();

const isUnsavedChangesModalVisible = ref<boolean>(false);
const skipUnsavedCheck = ref<boolean>(false);
const shouldDisplayUnsavedChangesModal = ref<boolean>(false);
const pendingRoute = ref<null | ReturnType<typeof router.resolve>>(null);

const defaultTab: BrandingTabType =
  (route.query?.tab as BrandingTabType) ?? "branding-tab-company-name";
const activeTab = ref<BrandingTabType>(defaultTab);

const selectedMsp = computed(() => authStore.selectedMsp);
const selectedAccount = computed(() => authStore.selectedAccount);

const setPageTitle = (title: string) =>
  pageMetaStore.setPageTitle({
    title,
  });

onMounted(() => {
  setPageTitle(t("branding"));
});

const handleHasChanged = (value: boolean) => {
  shouldDisplayUnsavedChangesModal.value = value;
};

onBeforeRouteLeave(async (to, _from, next) => {
  if (skipUnsavedCheck.value) {
    await brandingStore.removeUploadedLogos({
      organisationUuid:
        selectedMsp?.value?.uuid || selectedAccount?.value?.uuid,
    });
    skipUnsavedCheck.value = false;
    return next();
  }

  if (shouldDisplayUnsavedChangesModal.value) {
    isUnsavedChangesModalVisible.value = true;
    pendingRoute.value = router.resolve(to);
    return next(false);
  }

  next();
});

const handleLeaveWithoutApplyingChanges = () => {
  isUnsavedChangesModalVisible.value = false;
  skipUnsavedCheck.value = true;
  if (pendingRoute.value) {
    router.push(pendingRoute.value.fullPath).catch(() => {});
    pendingRoute.value = null;
  }
};

const handleStayWithChanges = () => {
  isUnsavedChangesModalVisible.value = false;
  pendingRoute.value = null;
};

watch(
  () => activeTab.value,
  () => {
    router.push({
      path: route.path,
      query: {
        ...route.query,
        tab: activeTab.value,
      },
    });
  },
  { immediate: true, deep: true },
);
</script>
